import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Lock, CreditCard, Truck, CheckCircle2, Zap, ArrowRight, ShieldCheck } from 'lucide-react';
// import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../admin/context/ProductStore';
import LoginModal from '../components/LoginModal';
import './Checkout.css';

export default function Checkout({ cart, setCart }) {
  const navigate = useNavigate();
  const { user, API_BASE } = useAuth();
  const { paymentGatewayEnabled, codEnabled } = useStore();
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ')[1] || '',
    email: user?.email || '',
    phone: user?.mobile || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentMethod: paymentGatewayEnabled ? 'upi' : (codEnabled ? 'cod' : '')
  });

  useEffect(() => {
    if (!paymentGatewayEnabled && (formData.paymentMethod === 'upi' || formData.paymentMethod === 'card')) {
      setFormData(prev => ({ ...prev, paymentMethod: codEnabled ? 'cod' : '' }));
    }
    if (!codEnabled && formData.paymentMethod === 'cod') {
      setFormData(prev => ({ ...prev, paymentMethod: paymentGatewayEnabled ? 'upi' : '' }));
    }
  }, [paymentGatewayEnabled, codEnabled]);

  const subtotal = cart.reduce((sum, item) => sum + item.price * (item.qty || 1), 0);
  const shipping = subtotal >= 599 ? 0 : 49;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePlaceOrder = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('koa_token');
      const orderPayload = {
        items: cart.map(item => ({
          product: item._id || item.id,
          name: item.name,
          price: item.price,
          qty: item.qty,
          image: item.image
        })),
        shippingAddress: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          phone: formData.phone
        },
        payment: {
          method: formData.paymentMethod,
          status: formData.paymentMethod === 'cod' ? 'pending' : 'paid'
        },
        totals: {
          subtotal,
          shipping,
          total
        },
        guestDetails: !user ? {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          mobile: formData.phone
        } : null
      };

      // ── IF COD ──
      if (formData.paymentMethod === 'cod') {
        const res = await fetch(`${API_BASE}/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(orderPayload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Order failed');
        setOrderSuccess(data);
        setCart([]);
        return;
      }

      // ── IF RAZORPAY (UPI/Card) ──
      const resLoad = await loadRazorpayScript();
      if (!resLoad) {
        alert('Razorpay SDK failed to load. Are you online?');
        return;
      }

      // 1. Create Razorpay Order
      const rzpOrderRes = await fetch(`${API_BASE}/orders/razorpay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: total })
      });
      const rzpOrder = await rzpOrderRes.json();
      if (!rzpOrderRes.ok) throw new Error(rzpOrder.error || 'Failed to create payment order');

      // 2. Open Razorpay Checkout
      const options = {
        key: 'rzp_live_SnzyVRHJphVWaH', // Use Key ID directly as requested
        amount: rzpOrder.amount,
        currency: rzpOrder.currency,
        name: 'KOA Dry Fruits',
        description: 'Premium Dry Fruits Purchase',
        order_id: rzpOrder.id,
        handler: async (response) => {
          try {
            // 3. Verify Payment
            const verifyRes = await fetch(`${API_BASE}/orders/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            const verifyData = await verifyRes.json();

            if (verifyRes.ok && verifyData.success) {
              // 4. Create Order in DB
              const finalOrderRes = await fetch(`${API_BASE}/orders`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                  ...orderPayload,
                  payment: { ...orderPayload.payment, status: 'paid', razorpay_id: response.razorpay_payment_id }
                })
              });
              const finalOrderData = await finalOrderRes.json();
              if (!finalOrderRes.ok) throw new Error(finalOrderData.error || 'Failed to finalize order');
              
              setOrderSuccess(finalOrderData);
              setCart([]);
            } else {
              alert('Payment verification failed. Please contact support.');
            }
          } catch (err) {
            alert(err.message || 'Payment processing failed.');
          }
        },
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        theme: { color: '#7a4e2d' },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      alert(err.message || 'Order placement failed. Please try again.');
    } finally {
      // Don't set loading false here if Razorpay is open, it's handled in ondismiss
      if (formData.paymentMethod === 'cod') setLoading(false);
    }
  };


  if (cart.length === 0 && !orderSuccess) {
    return (
      <div className="checkout-empty container anim-fade-up">
        <div className="empty-card">
          <Truck size={64} strokeWidth={1} />
          <h2>Your cart is waiting...</h2>
          <p>Add some premium nuts & dry fruits to continue.</p>
          <Link to="/shop" className="btn-primary">Shop Now</Link>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <main className="checkout-page success-page">
        <div className="container success-card anim-fade-up">
          <div className="success-lottie-wrap">
            <CheckCircle2 size={80} className="success-icon" />
          </div>
          <h1>Order Confirmed!</h1>
          <p>Thank you, {formData.firstName}. Your order <strong>#{orderSuccess.orderId}</strong> is being prepared.</p>
          
          <div className="success-next-steps">
            <div className="step-item">
              <div className="step-dot" />
              <div>
                <p className="step-title">Order Received</p>
                <p className="step-desc">We've sent a confirmation to {formData.email || formData.phone}</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-dot pulse" />
              <div>
                <p className="step-title">Processing</p>
                <p className="step-desc">Expected delivery in 3-5 business days</p>
              </div>
            </div>
          </div>

          <div className="success-actions">
            <button className="btn-primary" onClick={() => navigate('/')}>Back to Home</button>
            <button className="btn-outline" onClick={() => navigate(`/track-order?id=${orderSuccess.orderId}`)}>Track Order</button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <div className="container checkout-layout">
        <form onSubmit={handlePlaceOrder} className="checkout-main">
          {/* Quick Info Header */}
          <div className="checkout-quick-header">
            <Link to="/cart" className="back-link"><ChevronLeft size={16} /> Back</Link>
            <h1>Secure Checkout</h1>
            <div className="trust-badge-mini"><ShieldCheck size={14} /> 256-bit SSL</div>
          </div>

          {/* 1. Contact Info */}
          <section className="checkout-section">
            <div className="section-header-row">
              <span className="section-num">1</span>
              <h3>Contact Information</h3>
              {!user && (
                <p className="section-tip">
                  Already have an account? 
                  <span className="link-text" onClick={() => setLoginModalOpen(true)}> Login</span>
                </p>
              )}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <input type="text" name="firstName" placeholder="First Name" required value={formData.firstName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <input type="text" name="lastName" placeholder="Last Name" required value={formData.lastName} onChange={handleInputChange} />
              </div>
              <div className="form-group full">
                <input type="tel" name="phone" placeholder="Mobile Number (for delivery updates)" required value={formData.phone} onChange={handleInputChange} />
              </div>
              <div className="form-group full">
                <input type="email" name="email" placeholder="Email (Optional for tracking)" value={formData.email} onChange={handleInputChange} />
              </div>
            </div>
          </section>

          {/* 2. Shipping Address */}
          <section className="checkout-section">
            <div className="section-header-row">
              <span className="section-num">2</span>
              <h3>Delivery Address</h3>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <input type="text" name="address" placeholder="Address (House No, Building, Street, Area)" required value={formData.address} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <input type="text" name="city" placeholder="City" required value={formData.city} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <input type="text" name="state" placeholder="State" required value={formData.state} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <input type="text" name="pincode" placeholder="Pincode" required value={formData.pincode} onChange={handleInputChange} />
              </div>
            </div>
          </section>

          {/* 3. Payment */}
          <section className="checkout-section">
            <div className="section-header-row">
              <span className="section-num">3</span>
              <h3>Payment Method</h3>
            </div>
            <div className="payment-grid-compact">
              {paymentGatewayEnabled && (
                <>
                  <label className={`pay-card ${formData.paymentMethod === 'upi' ? 'active' : ''}`}>
                    <input type="radio" name="paymentMethod" value="upi" checked={formData.paymentMethod === 'upi'} onChange={handleInputChange} />
                    <Zap size={20} />
                    <span>UPI / QR</span>
                  </label>
                  <label className={`pay-card ${formData.paymentMethod === 'card' ? 'active' : ''}`}>
                    <input type="radio" name="paymentMethod" value="card" checked={formData.paymentMethod === 'card'} onChange={handleInputChange} />
                    <CreditCard size={20} />
                    <span>Card</span>
                  </label>
                </>
              )}
              {codEnabled && (
                <label className={`pay-card ${formData.paymentMethod === 'cod' ? 'active' : ''}`}>
                  <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleInputChange} />
                  <Truck size={20} />
                  <span>COD</span>
                </label>
              )}
            </div>
          </section>

          <div className="desktop-footer-action">
            <button type="submit" className="btn-primary checkout-btn-final" disabled={loading}>
              {loading ? 'Processing...' : `Place Order • ₹${total}`}
            </button>
          </div>
        </form>

        {/* Sidebar Summary */}
        <aside className="checkout-sidebar">
          <div className="summary-sticky">
            <h3 className="summary-title">Order Summary</h3>
            <div className="summary-items">
              {cart.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="item-thumb">
                    <img src={item.image} alt={item.name} />
                    <span className="item-qty">{item.qty}</span>
                  </div>
                  <div className="item-details">
                    <p className="item-name">{item.name}</p>
                    <p className="item-price">₹{item.price * item.qty}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="summary-totals">
              <div className="total-row"><span>Subtotal</span><span>₹{subtotal}</span></div>
              <div className="total-row"><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span></div>
              <div className="total-row grand-total"><span>Total</span><span>₹{total}</span></div>
            </div>
            <div className="checkout-guarantee">
              <Lock size={12} /> Secure Checkout Guaranteed
            </div>
          </div>
        </aside>

        {/* Mobile Sticky CTA */}
        <div className="mobile-checkout-bar">
          <div className="mobile-total">
            <p>Total</p>
            <h3>₹{total}</h3>
          </div>
          <button onClick={handlePlaceOrder} className="btn-primary mobile-place-btn" disabled={loading}>
            {loading ? '...' : 'Place Order'} <ArrowRight size={16} />
          </button>
        </div>
      </div>
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </main>
  );
}
